import { normalizeISO3 } from './geography';
import { COUNTRY_CODES } from '../common/data';
import { getMapCoordinates } from './mapCoordinates';
import { numericScore, qualitativeScoreLabel } from './scoreLabel';
import type { RiskEvent } from '@types';
import { isPublishedRiskEvent, OSINT_EVENT_NOTE } from '../../functions/src/common/riskEventPublication';

export const EVENT_PAGE_SIZE = 10;
export const EVENT_WINDOW_PAGES = 3;
export const COUNTRY_PIN_LIMIT = 100;
const riskValues = (event: any) => [event?.riskScore, event?.risk_score, event?.analysis?.risk_score,
    event?.overallGeopoliticalRiskScore, event?.score];

export function getEventRiskScore(event: any): number | null {
    const values = riskValues(event);
    for (const value of values) {
        const score = numericScore(value);
        if (score !== null)
            return score;
    }
    return null;
}

export const getEventRiskLabel = (event: any) => qualitativeScoreLabel([
    ...riskValues(event), event?.threatLevel, event?.riskLevel, event?.level, event?.riskRating, event?.overallRiskLevel,
]);

export function formatEventRiskScore(event: any): string {
    const score = getEventRiskScore(event);
    return score === null ? getEventRiskLabel(event) || 'Not assessed' : `${Math.round(score)}%`;
}
const countryLookup = new Map(Object.entries(COUNTRY_CODES).flatMap(([name, codes]) =>
    [[name.toUpperCase(), codes.iso3], [codes.iso2, codes.iso3], [codes.iso3, codes.iso3]]));

export function eventCountry(event: any): string | null {
    for (const value of [event?.iso3, event?.geo?.country_iso3, event?.location?.country, event?.country]) {
        if (typeof value !== 'string')
            continue;
        const code = countryLookup.get(normalizeISO3(value) || '');
        if (code)
            return code;
    }
    return null;
}

export function publishedEvent(data: any): boolean {
    return isPublishedRiskEvent(data);
}

// Keep feed records small; full reports remain in their dedicated views.
export function normalizeFeedEvent(id: string, data: any): RiskEvent {
    const riskScore = getEventRiskScore(data);
    const { lat, lng } = getMapCoordinates(data) || { lat: 0, lng: 0 };
    return {
        id, title: String(data.title || data.analysis?.intel_title || 'Intelligence Alert').slice(0, 600),
        summary: String(data.summary || data.analysis?.incident_summary || '').slice(0, 2000),
        riskScore, riskLevel: getEventRiskLabel(data),
        severity: data.severity || (riskScore === null ? 'unknown' : riskScore >= 80 ? 'critical' : riskScore >= 60 ? 'high' : riskScore >= 40 ? 'medium' : 'low'),
        riskJustification: data.riskJustification, methodology: data.methodology, scoreMethod: data.scoreMethod,
        date: data.date || data.pubDate || '', latitude: lat, longitude: lng,
        category: String(data.category || data.analysis?.category || 'General'),
        source: String(data.source || 'GDELT'), source_label: data.source_label,
        sourceUrl: data.source_url || data.sourceUrl || null, sourceNote: data.sourceNote || OSINT_EVENT_NOTE,
        primary_threat_actor: data.primary_threat_actor || data.analysis?.primary_threat_actor || null,
        location: { city: data.location_name || data.geo?.location_name || data.location?.city || 'Global',
            country: eventCountry(data) || 'UNK',
            lat, lng },
        verification: { statement: String(data.verification?.statement || data.verification_statement || '').slice(0, 1000),
            sources: (Array.isArray(data.verification?.sources) ? data.verification.sources : []).slice(0, 10) },
        verified: data.verified === true,
        vettingStatus: data.vettingStatus,
        vettingMethod: data.vettingMethod,
        vettingVersion: data.vettingVersion,
        vettingSources: (Array.isArray(data.vettingSources) ? data.vettingSources : []).slice(0, 10),
        vettingReasons: (Array.isArray(data.vettingReasons) ? data.vettingReasons : []).slice(0, 10),
        confidencePercent: data.confidencePercent,
        confidence_percentage: data.confidence_percentage,
        confidenceScore: data.confidenceScore,
        confidence_score: data.confidence_score,
        confidenceLevel: data.confidenceLevel,
        confidenceJustification: data.confidenceJustification,
        analystConfidence: data.analystConfidence,
        confidence: data.confidence,
        processedAt: data.processedAt, pubDate: data.pubDate || data.publishedAt || null,
    } as RiskEvent;
}

export function matchesFeed(event: RiskEvent, critical: boolean, category: string): boolean {
    return (!critical || (getEventRiskScore(event) ?? -1) >= 80 || getEventRiskLabel(event) === 'CRITICAL') &&
        (category === 'All' || !!event.category?.toLowerCase().includes(category.toLowerCase()));
}
